<template>
	<section class="marketing-section bg-color-content-primary text-color-white py-500-md">
		<div class="container">
			<div class="display-flex flex-direction-column-smo align-items-center gap-500-md gap-400-smo">
				<div class="col-12 col-6-md">
					<h2 class="overline text-color-gray-40">Our historical advisory client returns</h2>
					<h3 class="heading-1 text-color-white mt-100 mb-200">
						{{ headerText }}
					</h3>
					<p class="delta mb-100">
						Our $7+ billion investment portfolio* aims to harness the most powerful long-term drivers of the
						U.S. economy. In today's macroeconomic environment, we believe
						<BaseLink
							:link="fundrisePortfolioLink"
							classes="text-color-current-color text-decoration-underline text-link--dark-mode"
						/>
						is as well-positioned as any in the world to deliver stable, market-beating returns.
					</p>
					<BaseCtaLink classes="body-lg" :is-orange="true" icon="arrow" :link="clientReturnsLink" />
				</div>
				<div class="col-12 col-6-md">
					<div
						v-if="showSingleYearReturns"
						class="bg-color-greige rounded px-100 px-250-lg py-200 py-300-lg mx-200-lg mt-200-smo"
					>
						<BasePlaceHolder :placehold="!firstHalfReturns">
							<table v-if="firstHalfReturns" class="returns-table text-color-content-primary width-100">
								<thead>
									<th class="subheading text-align-center pb-100" colspan="2">
										<span class="heading-5 display-block">Annual returns</span>
										({{ firstHalfReturns.year }})
									</th>
								</thead>
								<tbody>
									<tr class="bt">
										<th class="py-50">
											Fundrise
											<span class="muted display-block font-weight-lighter zeta">
												(all clients)<sup>1</sup>
											</span>
										</th>
										<td class="text-align-right">
											<span
												:class="['heading-5', returnsColorClass(firstHalfReturns.fundrise)]"
												>{{ firstHalfReturns.fundrise }}</span
											>
										</td>
									</tr>
									<tr class="bt">
										<th class="py-50">
											Public REITs
											<span class="muted display-block font-weight-lighter zeta">
												(all U.S. REITs)<sup>2</sup>
											</span>
										</th>
										<td class="text-align-right">
											<span :class="['heading-5', returnsColorClass(firstHalfReturns.reits)]">{{
												firstHalfReturns.reits
											}}</span>
										</td>
									</tr>
									<tr class="bt">
										<th class="py-50">
											Public stocks
											<span class="muted display-block font-weight-lighter zeta">
												(S&amp;P 500)<sup>3</sup>
											</span>
										</th>
										<td class="text-align-right">
											<span :class="['heading-5', returnsColorClass(firstHalfReturns.stocks)]">{{
												firstHalfReturns.stocks
											}}</span>
										</td>
									</tr>
								</tbody>
							</table>
						</BasePlaceHolder>
					</div>
					<div v-else v-reveal:fade-up class="returns-snapshot">
						<h3 class="heading-5 text-align-center"> Recent Advisory Client Returns </h3>
						<AnnualReturnsTableSnapshotView />
					</div>
				</div>
			</div>
			<div class="mt-200-smo mt-300-md text-align-center muted body-xs">
				<p v-if="isWebview && isAndroidDevice">
					Learn more* about the assumptions in this section. The foregoing performance information is
					presented solely with regard to the advisory client performance of the clients of Fundrise Advisors,
					LLC pursuant to the Investment Advisers Act of 1940, and does not represent the performance of any
					individual investor or any individual or aggregate performance of any funds with offerings qualified
					pursuant Regulation A of the Securities Act (the "Regulation A Funds"). For more information about
					the Regulation A Funds, and their corresponding Forms 1-A, including their individual performance
					information, please click
					<BaseButton classes="text-link--dark-mode muted" @click="openDisclaimerModal()"> here</BaseButton>.
				</p>
				<p v-else>
					<BaseButton classes="text-link--dark-mode muted" @click="openModal">Learn more</BaseButton>
					about the assumptions in this section. The foregoing performance information is presented solely
					with regard to the advisory client performance of the clients of Fundrise Advisors, LLC pursuant to
					the Investment Advisers Act of 1940, and does not represent the performance of any individual
					investor or any individual or aggregate performance of any funds with offerings qualified pursuant
					Regulation A of the Securities Act (the "Regulation A Funds"). For more information about the
					Regulation A Funds, and their corresponding Forms 1-A, including their individual performance
					information, please click
					<BaseButton classes="text-link--dark-mode muted" @click="openDisclaimerModal()"> here</BaseButton>.
				</p>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
import { computed, Ref, ref } from 'vue';
import { annualReturnsDisclosureModalProps } from '@constants/annual-returns-data';
import { AnnualReturnsRow } from 'types/annual-returns';
import AnnualReturnsTableSnapshotView from '@components/charts/annual-returns-table-snapshot-view.vue';
import DisclaimerSimple from '../disclaimer/disclaimer-simple.vue';
import { getAnnualReturnsTableData } from '@api/sanity/graphql/charts-data';
import { isAndroidDevice as isAndroid } from '@utils/client';
import { Link } from 'types/layout';
import { regAFundListing } from '@constants/marketing-site';
import { useAppStore } from '@stores/app';

interface Props {
	headerText?: string;
	isWebview?: boolean;
	showSingleYearReturns?: boolean;
}

withDefaults(defineProps<Props>(), {
	headerText: 'Delivering strong results for our clients, even when public markets falter.',
	isWebview: false,
	showSingleYearReturns: true
});

const appStore = useAppStore();

const fundrisePortfolioLink: Link = {
	text: 'the Fundrise portfolio',
	router: 'strategy'
};
const firstHalfReturns: Ref<AnnualReturnsRow | null> = ref(null);

const clientReturnsLink: Link = {
	text: 'See all historical advisory client returns',
	router: 'client-returns'
};

const isAndroidDevice = computed((): boolean => {
	return isAndroid();
});

function returnsColorClass(value: string): string {
	return !!value && String(value).includes('-') ? 'text-color-red' : 'text-color-green';
}

function openModal(): void {
	appStore.updateCurrentModal({
		modal: DisclaimerSimple,
		props: annualReturnsDisclosureModalProps
	});
}

function openDisclaimerModal(): void {
	appStore.updateCurrentModal({
		modal: DisclaimerSimple,
		props: regAFundListing
	});
}

async function setup(): Promise<void> {
	try {
		const data = (await getAnnualReturnsTableData('annual-returns-section')) ?? null;
		firstHalfReturns.value = data?.tableData[0] ?? null;
	} catch {}
}
setup();
</script>
